<template>
  <view-wrapper
    name="Import Configurations"
    :loading="loading"
    :loadingText="loadingText"
  >
    <router-link
      class="mt-3 mb-12 button big primary white"
      to="importconfigurations/create"
      >Create new configuration
    </router-link>
    <modal
      v-if="confirmDeleteOpen"
      @close="confirmDeleteOpen = !confirmDeleteOpen"
      type="warning"
    >
      <template slot="body">
        <h3>Are you sure?</h3>
        <p>This action cannot be reverted</p>
      </template>
      <template slot="buttons">
        <grafokett-button
          class="mb-2 button big primary white"
          @click="confirmDeleteOpen = !confirmDeleteOpen"
          >Cancel
        </grafokett-button>
        <grafokett-button
          class="mb-2 button big primary white"
          @click="deleteConfig()"
          >Delete
        </grafokett-button>
      </template>
    </modal>

    <expandabel-table
      :data="filteredImportConfigurations"
      :headers="headers"
      :columnsToNotSort="columnsToNotSort"
      @searchQuery="searchText = $event"
    >
      <template v-slot:column="{ column, child }">
        <div v-if="column === 'name'">
          {{ child[column] }}
        </div>
        <div v-if="column === 'created' || column === 'modified'">
          {{ formatDate(child[column]) }}
        </div>
        <div v-if="column === 'default' && child[column]">
          <span
            class="px-2 py-1 text-xs font-semibold text-white uppercase rounded-full bg-highlight"
            >Default</span
          >
        </div>
      </template>
      <template v-slot:child="{ child }">
        <div class="flex flex-col w-full">
          <div class="flex items-center ml-auto text-highlight">
            <router-link
              :to="`importconfigurations/${child.id}/edit`"
              class="flex items-center mr-4 text-sm no-underline text-highlight hover:font-bold"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                />
              </svg>
              <span>Edit</span>
            </router-link>
            <button
              class="flex items-center p-0 mr-4 text-sm bg-transparent border-none text-highlight hover:font-bold"
              @click="setAsDefault(child)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>
                Set Default
              </span>
            </button>
            <button
              class="flex items-center p-0 text-sm bg-transparent border-none text-highlight hover:font-bold"
              @click="openConfirmModal(child)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Delete</span>
            </button>
          </div>
          <p class="w-full mb-2 font-bold">Base Settings</p>
          <div class="flex p-4 rounded-lg bg-primary-light">
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Header Line Number</span>
              <span class="text-gray-light">{{ child.headersLineNumber }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Data Line Number</span>
              <span class="text-gray-light">{{ child.dataStartsAtLine }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Delimiter</span>
              <span class="text-gray-light">{{ child.delimiter }}</span>
            </div>
          </div>
          <p class="w-full mb-2 font-bold">Order Settings</p>
          <div class="flex p-4 rounded-lg bg-primary-light">
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Uses Orders</span>
              <span class="text-gray-light">{{
                child.usesOrders ? "Yes" : "No"
              }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Order Reference Column</span>
              <span class="text-gray-light">{{
                child.orderReferenceColumn
              }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Default Order Data JSON</span>
              <span class="text-gray-light">{{
                child.defaultOrderDataJson
              }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Default Orderreference</span>
              <span class="text-gray-light">{{
                child.defaultOrderReference
              }}</span>
            </div>
          </div>
          <p class="w-full mb-2 font-bold">Article Settings</p>
          <div class="flex p-4 rounded-lg bg-primary-light">
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Article Name Columns</span>
              <span class="text-gray-light">{{
                child.articleNameColumns
              }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">External Id Column</span>
              <span class="text-gray-light">{{ child.externalIdColumn }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Article Quantity Column</span>
              <span class="text-gray-light">{{
                child.articleQuantityColumn
              }}</span>
            </div>
          </div>

          <p class="w-full mb-2 font-bold">Group Settings</p>
          <div class="flex p-4 rounded-lg bg-primary-light">
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Groups Column</span>
              <span class="text-gray-light">{{ child.groupsColumn }}</span>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-xs font-bold">Default Groups</span>
              <span class="text-gray-light">{{
                getGroupString(child.defaultGroupIds)
              }}</span>
            </div>
            <div class="flex flex-col flex-1"></div>
          </div>
        </div>
        <p class="w-full mb-2 font-bold">Order Keys</p>
        <div class="flex flex-col p-4 rounded-lg bg-primary-light">
          <div
            v-for="(key, index) in child.orderKeys"
            :key="index"
            class="bg-primary rounded my-2 p-4"
          >
            {{ key.key }}
          </div>
        </div>
      </template>
    </expandabel-table>
  </view-wrapper>
</template>

<script>
import ExpandabelTable from "../../components/ExpandabelTable";
import GrafokettButton from "../../components/forms/Button.vue";
import Modal from "../../components/Modal";
import { formatDate } from "../../utils/dateHelpers";

export default {
  components: {
    ExpandabelTable,
    GrafokettButton,
    Modal,
  },
  data() {
    return {
      loading: false,
      loadingText: "List of Import Configurations",
      importConfigurations: [],
      headers: {
        name: "Name",
        created: "Created",
        modified: "Last edited",
        default: "",
      },
      columnsToNotSort: ["default"],
      confirmDeleteOpen: false,
      configToDelete: null,
      searchText: "",
    };
  },
  methods: {
    async getData() {
      this.loading = true;

      const { data } = await this.$store.$api.get("/api/importconfigurations");
      this.importConfigurations = data.data.map(config => ({
        id: config.id,
        name: config.name,
        created: config.created,
        modified: config.modified,
        default: config.default,
        expandableData: { ...config },
      }));

      const { data: groupsData } = await this.$store.$api.get("/api/groups");
      this.groups = groupsData.data.map(({ id, name }) => ({ id, name }));

      this.loading = false;
    },
    async setAsDefault(child) {
      try {
        await this.$store.$api.put("api/importconfigurations/default", {
          importConfigurationId: child.id,
        });

        const configurations = this.importConfigurations.filter(
          x => x.id !== child.id
        );

        configurations.forEach(x => (x.default = false));

        const defaultConfiguration = this.importConfigurations.find(
          x => x.id === child.id
        );

        defaultConfiguration.default = true;
      } catch (error) {
        error;
      }
    },
    openConfirmModal(configToDelete) {
      this.confirmDeleteOpen = true;
      this.configToDelete = configToDelete;
    },
    async deleteConfig() {
      this.confirmDeleteOpen = false;
      await this.$store.$api.delete(
        `/api/importconfigurations/${this.configToDelete.id}`
      );

      this.importConfigurations = [
        ...this.importConfigurations.filter(
          x => x.id !== this.configToDelete.id
        ),
      ];
    },
    formatDate: formatDate,
    getGroupString(groupIdsString) {
      const groupIds = groupIdsString.split(",").map(id => id.trim());

      const groups = this.groups.filter(group =>
        groupIds.includes(group.id.toString())
      );

      return groups.map(x => x.name).join(", ");
    },
  },
  async mounted() {
    await this.getData();
  },
  computed: {
    filteredImportConfigurations() {
      return this.importConfigurations.filter(
        x => x.name.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
      );
    },
  },
};
</script>

<style></style>
